import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import * as HttpStatus from 'http-status-codes'
import { firstValueFrom, Observable, of, ReplaySubject } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { User } from '../interfaces/resources/user.interface'
import { FlashMessageService } from './flash-message.service'
import { HelperService } from './helper.service'
import { KeycloakService } from './keycloak.service'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Lazy inject HttpClient to avoid circular dependency
  private http = inject(HttpClient)
  private flashMessageService = inject(FlashMessageService)
  private keycloakService = inject(KeycloakService)

  permissions: string[]
  baseUrl = `${environment.apiBaseUrl}/auth`

  public currentUser = new ReplaySubject(1)
  public tokenAllowedDomains: string[]

  constructor() {
    this.tokenAllowedDomains = environment.tokenAllowedDomains
  }

  async loginWithKeycloak(redirectUri?: string): Promise<void> {
    this.keycloakService.login(redirectUri)
  }

  isLoggedIn(): boolean {
    return this.keycloakService.isAuthenticated()
  }

  getToken(): string {
    return this.keycloakService.getToken() || ''
  }

  // Get Current user permissions from Service or from remote API.
  getPermissions(): Promise<string[]> {
    if (this.permissions) {
      return Promise.resolve(this.permissions)
    } else {
      return firstValueFrom(this.me())
        .then((userRes: User) => userRes.role.permissions.map((p) => p.name))
    }
  }

  // Check if current user have a specific permission
  async can(permission: string): Promise<boolean> {
    const userPermissions: string[] = await this.getPermissions()

    return (
      userPermissions &&
      userPermissions.length &&
      userPermissions.includes(permission)
    )
  }

  logout(): void {
    this.keycloakService.logout()
  }

  me(): Observable<User | boolean> {
    return this.http.get(`${this.baseUrl}/me`).pipe(
      map((userRes: User) => {
        // Store permissions in service.
        this.permissions = userRes.role.permissions.map((p) => p.name)
        return userRes
      }),
      catchError((err: HttpErrorResponse) => {
        console.log('err', err)
        if (err.status !== HttpStatus.StatusCodes.FORBIDDEN) {
          this.flashMessageService.error(
            'Error getting server information. Please try again and reload the page.'
          )
        }
        return of(false)
      })
    )
  }

  updateTransparencyReportProfile(data: { organizationName: string; country: string; website: string }): Observable<any> {
    return this.http.patch(`${this.baseUrl}/transparency-report-profile`, data)
  }

  getCreateResourcesPermissionName(resourceSlug: string): string {
    return `add${HelperService.classify(resourceSlug)}`
  }
}
